import { USE_RECORDING_V2 } from '@/components/constants/flags';
import { waitUntil } from '@/helpers/waitUntil';
import { UploadStatus } from '@/models/tauriRecordModel';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import chunkAudioRecorder from '@/utils/chunkAudioRecorder';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getUploadProgress, waitForChunks } from '@heidi/capture/waitForChunks';
import {
  BackupUploadingStatus,
  type QuerySessionResponse,
} from '@heidi/session/contracts';

export type WaitForChunksParams = {
  sessionID: string;
  queryKey: readonly [string, { sessionId: string }];
  disableChunkRecording?: boolean;
  isCreatingForContextOnlySession?: boolean;
  isRegenerate?: boolean;
  sessionFromCache?: QuerySessionResponse;
  isChunkRecordingEnabled: boolean;
  signal?: AbortSignal;
};

type UploadProgressState = {
  isComplete: boolean;
  progress: number;
};

const RECORDING_V2_PROGRESS_POLL_INTERVAL_MS = 500;

const clampProgress = (progress: number) => Math.max(0, Math.min(100, progress));

const setChunkUploadProgress = (
  queryKey: WaitForChunksParams['queryKey'],
  progress: number
) => {
  reactQueryClient.setQueryData<QuerySessionResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return undefined;
    }

    return {
      ...oldData,
      chunkUploadProgress: clampProgress(progress),
    };
  });
};

const getRecordingV2UploadProgressState = async (
  sessionID: string
): Promise<UploadProgressState> => getUploadProgress(sessionID);

const abortableSleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error('Upload progress polling was aborted');
      error.name = 'AbortError';
      reject(error);
      return;
    }
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
      const error = new Error('Upload progress polling was aborted');
      error.name = 'AbortError';
      reject(error);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Waits for audio chunks to finish uploading before proceeding with note generation.
 * This ensures all audio data is available on the server before generating the note.
 */
export async function waitForChunksToFinishUploading({
  sessionID,
  queryKey,
  disableChunkRecording,
  isCreatingForContextOnlySession,
  isRegenerate,
  sessionFromCache,
  isChunkRecordingEnabled,
  signal,
}: WaitForChunksParams): Promise<void> {
  if (isTauri()) {
    await waitUntil(
      () => {
        const chunksUploaded = store
          .getState()
          .tauriRecordModel.chunks.blobs.every(
            (chunk) =>
              chunk.status === UploadStatus.Completed ||
              chunk.status === UploadStatus.Failed
          );
        const backupUploaded = store.getState().tauriRecordModel.chunks.backup_uploaded;
        return Boolean(chunksUploaded || backupUploaded);
      },
      undefined,
      undefined,
      signal
    );
  } else if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    USE_RECORDING_V2() &&
    !isCreatingForContextOnlySession
  ) {
    let shouldStopPolling = false;

    const progressPollingPromise = (async () => {
      while (!shouldStopPolling) {
        try {
          const uploadState = await getRecordingV2UploadProgressState(sessionID);
          setChunkUploadProgress(queryKey, uploadState.progress);

          if (uploadState.isComplete) {
            return;
          }
        } catch (err) {
          logger.error(
            '[waitForChunksToFinishUploading] Progress polling error, stopping poll',
            { sessionID, error: err }
          );
          return;
        }

        await abortableSleep(RECORDING_V2_PROGRESS_POLL_INTERVAL_MS, signal);
      }
    })();

    try {
      await waitForChunks(sessionID, signal);
    } finally {
      shouldStopPolling = true;
      await progressPollingPromise;
    }
  } else if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    !isCreatingForContextOnlySession
  ) {
    const shouldWaitForChunks =
      isRegenerate === false || sessionFromCache?.chunksUploadingFinished !== undefined;

    if (shouldWaitForChunks) {
      await waitUntil(
        () => {
          const sessionCacheUpdated =
            reactQueryClient.getQueryData<QuerySessionResponse>(queryKey);
          const backupUploaded =
            sessionCacheUpdated?.backupUploadingStatus === BackupUploadingStatus.SUCCESS;

          const areChunksUploaded =
            chunkAudioRecorder.allChunksUploaded() &&
            sessionCacheUpdated?.chunksUploadingFinished;
          const isBackupUsed =
            chunkAudioRecorder.chunkHasIssues() &&
            sessionCacheUpdated?.backupUploadingStatus === BackupUploadingStatus.SUCCESS;

          return Boolean(areChunksUploaded || isBackupUsed || backupUploaded);
        },
        undefined,
        undefined,
        signal
      );
    }
  }

  if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    USE_RECORDING_V2() &&
    !isCreatingForContextOnlySession
  ) {
    try {
      const uploadState = await getRecordingV2UploadProgressState(sessionID);
      setChunkUploadProgress(queryKey, uploadState.progress);
    } catch (err) {
      // A store that cannot answer must not fail note generation: the wait is
      // already over, so the last polled progress stands.
      logger.error(
        '[waitForChunksToFinishUploading] Final progress read failed, keeping last progress',
        { sessionID, error: err }
      );
    }
  }
}
